import FormattedText from '@nespresso/catalog-components-react/containers/FormattedText';
import { Icon, type IconPath } from '@nespresso-design-system/components';
import { trackAboutSubscriptionsModalInteraction } from '@/widgets/ProductDetailsHero/Tracking';
import type { Product } from '@/domain/Product';
import useStaticContent from '@/widgets/hooks/useStaticContent.tsx';
import useTranslations from '@/widgets/hooks/useTranslations.tsx';
import styles from './AboutSubscription.module.scss';
import type { Subscription } from '@/domain/Subscription';
import Accordion from '@/widgets/components/containers/Accordion/Accordion.tsx';

export default function AboutSubscription({
  product,
  selectedSubscription
}: {
  product: Product;
  selectedSubscription?: Subscription;
}) {
  const { t } = useTranslations();
  const { aboutSubscriptions } = useStaticContent();

  const handleMoreInfoClick = () => {
    trackAboutSubscriptionsModalInteraction(
      {
        key: 'aboutSubscriptions.moreInfoLink.label',
        value: aboutSubscriptions?.moreInfoLink?.label ?? ''
      },
      product,
      selectedSubscription
    );
  };

  // TODO: Polish event implementation (ITNP-153135) - deferred to future work
  const handleTermsToggled = () =>
    trackAboutSubscriptionsModalInteraction(
      {
        key: 'productDetailsHero.drawers.subscriptionOptionsDrawer.termsAndConditions.label',
        value: ''
      },
      product,
      selectedSubscription
    );

  if (!aboutSubscriptions?.features?.length) return null;

  return (
    <section class={styles.section}>
      <div class={styles.introduction}>
        <FormattedText>{aboutSubscriptions.description}</FormattedText>
      </div>
      <ul class={styles.features}>
        {aboutSubscriptions.features.map((feature) => (
          <li key={feature.icon} class={styles.feature}>
            <Icon icon={`${feature.icon}` as IconPath} />
            <FormattedText>{feature.description}</FormattedText>
          </li>
        ))}
      </ul>
      {aboutSubscriptions.moreInfoLink && (
        <a
          class={styles.moreInfo}
          href={aboutSubscriptions.moreInfoLink?.url}
          target="_blank"
          rel="noreferrer"
          aria-label={aboutSubscriptions.moreInfoLink?.label}
          onClick={handleMoreInfoClick}
        >
          {aboutSubscriptions.moreInfoLink?.label}
        </a>
      )}
      {aboutSubscriptions.termsAndConditions && (
        <Accordion size="small">
          <Accordion.Item
            onToggle={handleTermsToggled}
            title={t(
              'productDetailsHero.drawers.subscriptionOptionsDrawer.termsAndConditions.label'
            )}
          >
            <FormattedText>
              {aboutSubscriptions.termsAndConditions}
            </FormattedText>
          </Accordion.Item>
        </Accordion>
      )}
      {aboutSubscriptions?.faqs?.length > 0 && (
        <Accordion className={styles.faq}>
          <h3>
            {t(
              'productDetailsHero.drawers.subscriptionOptionsDrawer.FAQ.title'
            )}
          </h3>
          {aboutSubscriptions.faqs.map(({ question, answer }) => (
            <Accordion.Item
              name="faq"
              title={<FormattedText>{question}</FormattedText>}
            >
              <FormattedText>{answer}</FormattedText>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
    </section>
  );
}
